/*
 * LT-14 Phase 4d — Per-tender Payment Schedule tab.
 *
 * What lives here:
 *   - Schedule table (deposit + stages + totals) with per-stage
 *     adjustment % and lock toggle. Deposit is always the first row
 *     and always locked (a stated commercial term, not a balancing
 *     figure).
 *   - Deposit % control.
 *   - Allocation editor (per-phase dropdown or split; per-keyword
 *     dropdown or split; add-a-keyword combines keyword + milestone
 *     in one action, no seed-at-100%-on-first surprise).
 *   - Warnings surface, errors block.
 *
 * £0 IS THE NORMAL STATE OF EVERY NEW TENDER — you create it, then
 * price it over the following days. The tab renders the milestone
 * structure anyway (from the snapshot the backend produced at create
 * / lazy-backfill time). Message says "Price the estimate and the
 * stage figures will appear here" — not an error, not red.
 *
 * Deposit %, adjustments and locks all PERSIST on the tender
 * snapshot. The 4e PDF export will read the same fields. Storing
 * them only in this component's state would let the PDF drift
 * silently — same-inputs-in / same-numbers-out is the whole point.
 *
 * No writeback to Settings. Editing here is a snapshot edit, never
 * a template edit.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Save, Plus, Lock, GitBranch, X, ChevronUp, ChevronDown,
} from "lucide-react";
import LockOpen from "lucide-react/dist/esm/icons/lock-open";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Card, Field, Input, NumberInput, PrimaryButton, QuietButton,
} from "../../components/UI";

const cls = (...xs) => xs.filter(Boolean).join(" ");

// Matches ALLOCATION_TOLERANCE in payment_schedule.py.
const TOL = 0.001;

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function PaymentScheduleTab({ tender }) {
  const tenderId = tender.id;
  const [data, setData] = useState(null);        // full GET response
  const [depositPct, setDepositPct] = useState("10");
  const [adjustments, setAdjustments] = useState({});   // {milestoneId: pct}
  const [locked, setLocked] = useState(new Set());      // Set<milestoneId>
  const [dirtySettings, setDirtySettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Allocation editor is its own dirty-state so users can save
  // schedule settings without also saving in-progress allocation
  // edits (or vice-versa).
  const [editMilestones, setEditMilestones] = useState(null);
  const [dirtyMilestones, setDirtyMilestones] = useState(false);
  const [savingMilestones, setSavingMilestones] = useState(false);
  const [splitPhases, setSplitPhases] = useState(new Set());
  const [splitKw, setSplitKw] = useState(new Set());
  const [showAllocations, setShowAllocations] = useState(false);

  const applyResponse = (r, opts = {}) => {
    // Two save flows land here — saveSettings and saveAllocations —
    // and each returns the full snapshot so callers can reflect the
    // authoritative post-save state. Naively resetting everything on
    // both flows silently discards in-progress edits in the OTHER
    // half of the tab (e.g. save deposit % → allocation splits blown
    // away, no toast, no dirty cue). Callers now scope which slice
    // to sync:
    //   opts.only = "settings"   → refresh deposit/adjust/lock only
    //   opts.only = "milestones" → refresh milestone allocations only
    //   otherwise                → full reset (initial load + 409 retry)
    setData(r);
    const snap = r.snapshot || {};
    const applyAll = !opts.only;
    if (applyAll || opts.only === "settings") {
      setDepositPct(String(snap.deposit_pct ?? 10));
      setAdjustments({ ...(snap.adjustments || {}) });
      setLocked(new Set(snap.locked || []));
      setDirtySettings(false);
    }
    if (applyAll || opts.only === "milestones") {
      setEditMilestones((snap.milestones || []).map((m) => ({
        id: m.id, milestone_id: m.milestone_id, title: m.title,
        display_order: m.display_order,
        claims: { ...(m.claims || {}) },
        claims_match: { ...(m.claims_match || {}) },
      })));
      setSplitPhases(new Set());
      setSplitKw(new Set());
      setDirtyMilestones(false);
    }
  };

  const load = () =>
    api.get(`/tenders/${tenderId}/payment-schedule`)
      .then((r) => applyResponse(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load payment schedule"));

  useEffect(() => { load(); /* eslint-disable-line */ }, [tenderId]);

  /* ---------------- schedule-settings save ---------------- */
  const saveSettings = async () => {
    if (savingSettings) return;
    setSavingSettings(true);
    try {
      const r = await api.patch(`/tenders/${tenderId}/payment-schedule`, {
        deposit_pct: Number(depositPct || 0),
        adjustments: adjustments,
        locked: Array.from(locked),
      });
      applyResponse(r.data, { only: "settings" });
      toast.success("Schedule settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const setAdj = (mid, pct) => {
    setAdjustments((prev) => {
      const next = { ...prev };
      const n = Number(pct);
      if (pct === "" || Number.isNaN(n)) delete next[mid];
      else next[mid] = n;
      return next;
    });
    setDirtySettings(true);
  };

  const toggleLocked = (mid) => {
    setLocked((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
    setDirtySettings(true);
  };

  /* ---------------- allocation helpers (immutable) ---------------- */
  const totalFor = (kind, itemKey) => {
    const field = kind === "phase" ? "claims" : "claims_match";
    return editMilestones.reduce(
      (sum, m) => sum + ((m[field] || {})[itemKey] || 0), 0
    );
  };

  const nonZero = (kind, itemKey) => {
    const field = kind === "phase" ? "claims" : "claims_match";
    const out = [];
    for (const m of editMilestones) {
      const w = (m[field] || {})[itemKey] || 0;
      if (w > TOL) out.push({ milestone_id: m.id, weight: w });
    }
    return out;
  };

  const setWeight = (kind, itemKey, milestoneRowId, weight) => {
    const field = kind === "phase" ? "claims" : "claims_match";
    setEditMilestones((prev) => prev.map((m) => {
      if (m.id !== milestoneRowId) return m;
      const nx = { ...(m[field] || {}) };
      if (weight > TOL) nx[itemKey] = Number(weight);
      else delete nx[itemKey];
      return { ...m, [field]: nx };
    }));
    setDirtyMilestones(true);
  };

  const setSingle = (kind, itemKey, milestoneRowId) => {
    const field = kind === "phase" ? "claims" : "claims_match";
    setEditMilestones((prev) => prev.map((m) => {
      const nx = { ...(m[field] || {}) };
      delete nx[itemKey];
      if (milestoneRowId && m.id === milestoneRowId) nx[itemKey] = 1.0;
      return { ...m, [field]: nx };
    }));
    setDirtyMilestones(true);
  };

  const enterSplit = (kind, itemKey) => {
    if (kind === "phase") setSplitPhases((s) => new Set(s).add(itemKey));
    else                  setSplitKw((s) => new Set(s).add(itemKey));
  };

  const unsplit = (kind, itemKey) => {
    // Snap-and-drop. Compute survivor, write in one mutation — no
    // intermediate under-allocated state that a race could persist.
    const survivors = nonZero(kind, itemKey);
    if (survivors.length > 0) {
      const biggest = survivors.reduce(
        (a, b) => (a.weight >= b.weight ? a : b),
      );
      const field = kind === "phase" ? "claims" : "claims_match";
      setEditMilestones((prev) => prev.map((m) => {
        const nx = { ...(m[field] || {}) };
        delete nx[itemKey];
        if (m.id === biggest.milestone_id) nx[itemKey] = 1.0;
        return { ...m, [field]: nx };
      }));
      setDirtyMilestones(true);
    }
    if (kind === "phase") {
      setSplitPhases((s) => { const nx = new Set(s); nx.delete(itemKey); return nx; });
    } else {
      setSplitKw((s) => { const nx = new Set(s); nx.delete(itemKey); return nx; });
    }
  };

  const removeKeyword = (kw) => {
    setEditMilestones((prev) => prev.map((m) => {
      const nx = { ...(m.claims_match || {}) };
      delete nx[kw];
      return { ...m, claims_match: nx };
    }));
    setDirtyMilestones(true);
  };

  const addKeyword = (kw, milestoneRowId) => {
    const clean = (kw || "").trim().toLowerCase();
    if (!clean || !milestoneRowId) return;
    if (editMilestones.some((m) => (m.claims_match || {})[clean])) {
      toast.error(`Keyword "${clean}" already in use`);
      return;
    }
    setSingle("kw", clean, milestoneRowId);
  };

  const invalidItems = useMemo(() => {
    if (!editMilestones) return [];
    const bad = [];
    const phaseIds = new Set();
    const kwSet = new Set();
    for (const m of editMilestones) {
      for (const k of Object.keys(m.claims || {})) phaseIds.add(k);
      for (const k of Object.keys(m.claims_match || {})) kwSet.add(k);
    }
    const phaseNameById = Object.fromEntries(
      (tender.phases || []).map((p) => [p.id, p.name || p.id])
    );
    for (const pid of phaseIds) {
      const t = totalFor("phase", pid);
      if (t > TOL && Math.abs(t - 1.0) > TOL) {
        bad.push({ kind: "phase", id: pid, label: phaseNameById[pid] || pid, total: t });
      }
    }
    for (const kw of kwSet) {
      const t = totalFor("kw", kw);
      if (t > TOL && Math.abs(t - 1.0) > TOL) {
        bad.push({ kind: "kw", id: kw, label: `"${kw}"`, total: t });
      }
    }
    return bad;
  }, [editMilestones, tender.phases]);

  const saveAllocations = async () => {
    if (invalidItems.length > 0 || !dirtyMilestones || savingMilestones) return;
    setSavingMilestones(true);
    try {
      const r = await api.put(
        `/tenders/${tenderId}/payment-schedule/milestones`,
        {
          milestones: editMilestones.map((m, idx) => ({
            id: m.id, milestone_id: m.milestone_id, title: m.title,
            display_order: idx,
            claims: m.claims || {},
            claims_match: m.claims_match || {},
          })),
          expected_updated_at: data?.snapshot?.updated_at || null,
        },
      );
      applyResponse(r.data, { only: "milestones" });
      toast.success("Allocations saved");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("Schedule was updated elsewhere — reloading.");
        load();
      } else {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    } finally {
      setSavingMilestones(false);
    }
  };

  /* ---------------- render ---------------- */

  if (!data) {
    return <div className="mt-6 text-ink-3" style={{ fontSize: 12 }}>Loading payment schedule…</div>;
  }

  const snap = data.snapshot;
  if (!snap) {
    return (
      <div className="mt-6">
        <Card className="p-5" data-testid="payment-schedule-empty">
          <h3 className="font-display text-ink mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
            Payment Schedule
          </h3>
          <p className="text-ink-3" style={{ fontSize: 12 }}>
            {(data.warnings && data.warnings[0]) || "No schedule available."}
          </p>
        </Card>
      </div>
    );
  }

  const ctx = data.context;
  const rows = ctx?.rows || [];
  const totals = ctx?.totals || {};
  const hint = data.hint;
  const warnings = data.warnings || [];
  const errors = [...(data.errors || []), ...(data.allocation_errors || [])];
  const nonSystemPhases = (tender.phases || []).filter((p) => !p.is_system_phase);

  return (
    <div className="mt-6 flex flex-col gap-5" data-testid="payment-schedule-tab">
      {/* Header */}
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              Payment Schedule
            </h3>
            <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
              Snapshotted from the {snap.job_type} template. Editing here does not affect the Settings default.
            </p>
          </div>
          <PrimaryButton
            onClick={saveSettings}
            disabled={!dirtySettings || savingSettings}
            icon={savingSettings ? Loader2 : Save}
            data-testid="payment-schedule-save-settings"
          >
            Save schedule settings
          </PrimaryButton>
        </div>

        <div className="mt-5 flex items-end gap-6">
          <div className="flex flex-col gap-1">
            <span className="col-head" style={{ fontSize: 10 }}>Deposit</span>
            <div className="flex items-center gap-1">
              <NumberInput
                value={depositPct}
                onChange={(v) => { setDepositPct(v); setDirtySettings(true); }}
                step="0.1"
                min={0}
                max={100}
                suffix="%"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="col-head" style={{ fontSize: 10 }}>Contract total (ex VAT)</span>
            <span className="font-mono tnum text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              {fmtMoney(totals.contract_total)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="col-head" style={{ fontSize: 10 }}>Schedule balanced?</span>
            <span
              className={cls(
                "font-mono tnum",
                totals.balanced ? "text-note-green" : "text-note-red",
              )}
              style={{ fontSize: 13, fontWeight: 600 }}
              data-testid="payment-schedule-balanced"
            >
              {totals.balanced ? "Yes" : "No"}
            </span>
          </div>
        </div>
      </Card>

      {/* Hint (£0 state — never an error) */}
      {hint && (
        <div
          className="rounded-md border border-accent/30 bg-accent/5 px-4 py-3 text-ink-2"
          style={{ fontSize: 12 }}
          data-testid="payment-schedule-hint"
        >
          {hint}
        </div>
      )}

      {/* Schedule table */}
      <Card className="p-0" allowOverflow>
        <div className="grid grid-cols-[36px_1fr_120px_100px_60px_140px_120px_140px] gap-x-3 px-4 py-2 border-b border-line bg-surface-2 col-head" style={{ fontSize: 10 }}>
          <div>#</div>
          <div>Milestone</div>
          <div className="text-right">Cost</div>
          <div className="text-right">Adj %</div>
          <div className="text-center">Lock</div>
          <div className="text-right">Ex VAT</div>
          <div className="text-right">VAT</div>
          <div className="text-right">Inc VAT</div>
        </div>
        {rows.map((row, idx) => (
          <div
            key={row.key}
            className="grid grid-cols-[36px_1fr_120px_100px_60px_140px_120px_140px] gap-x-3 px-4 py-2 border-b border-line-soft items-center"
            data-testid={`payment-schedule-row-${row.key}`}
          >
            <div className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>
              {String(idx).padStart(2, "0")}
            </div>
            <div className="text-ink" style={{ fontSize: 13 }}>
              {row.title}
              {row.is_deposit && (
                <span className="ml-2 rounded-full px-1.5 py-0.5 bg-surface-2 text-ink-3" style={{ fontSize: 10 }}>
                  deposit
                </span>
              )}
            </div>
            <div className="text-right font-mono tnum text-ink-3" style={{ fontSize: 12 }}>
              {row.cost_value == null ? "—" : fmtMoney(row.cost_value)}
            </div>
            <div className="text-right">
              {row.is_deposit ? (
                <span className="text-ink-4" style={{ fontSize: 12 }}>—</span>
              ) : (
                <NumberInput
                  value={adjustments[row.key] ?? ""}
                  onChange={(v) => setAdj(row.key, v)}
                  step="0.1"
                  suffix="%"
                />
              )}
            </div>
            <div className="text-center">
              {row.is_deposit ? (
                <Lock size={12} className="text-ink-4 mx-auto" />
              ) : (
                <button
                  type="button"
                  onClick={() => toggleLocked(row.key)}
                  className={cls(
                    "h-7 w-7 rounded inline-flex items-center justify-center",
                    locked.has(row.key)
                      ? "text-accent bg-accent/10 hover:bg-accent/20"
                      : "text-ink-3 hover:bg-surface-2",
                  )}
                  title={locked.has(row.key) ? "Locked — will not absorb another stage's delta" : "Unlocked"}
                  data-testid={`payment-schedule-lock-${row.key}`}
                >
                  {locked.has(row.key) ? <Lock size={12} /> : <LockOpen size={12} />}
                </button>
              )}
            </div>
            <div className="text-right font-mono tnum text-ink" style={{ fontSize: 12 }}>
              {fmtMoney(row.ex_vat)}
            </div>
            <div className="text-right font-mono tnum text-ink-3" style={{ fontSize: 12 }}>
              {fmtMoney(row.vat)}
            </div>
            <div className="text-right font-mono tnum text-ink" style={{ fontSize: 12, fontWeight: 500 }}>
              {fmtMoney(row.inc_vat)}
            </div>
          </div>
        ))}
        <div className="grid grid-cols-[36px_1fr_120px_100px_60px_140px_120px_140px] gap-x-3 px-4 py-2.5 bg-surface-2 items-center">
          <div></div>
          <div className="text-ink font-medium" style={{ fontSize: 12 }}>Totals</div>
          <div></div>
          <div></div>
          <div></div>
          <div className="text-right font-mono tnum text-ink" style={{ fontSize: 12, fontWeight: 600 }}>
            {fmtMoney(totals.ex_vat)}
          </div>
          <div className="text-right font-mono tnum text-ink-3" style={{ fontSize: 12 }}>
            {fmtMoney(totals.vat)}
          </div>
          <div className="text-right font-mono tnum text-ink" style={{ fontSize: 12, fontWeight: 600 }}>
            {fmtMoney(totals.inc_vat)}
          </div>
        </div>
      </Card>

      {/* Warnings + errors */}
      {(warnings.length > 0 || errors.length > 0) && (
        <div className="flex flex-col gap-2">
          {warnings.map((w, i) => (
            <div
              key={`w-${i}`}
              className="rounded-md border border-note-amber/40 bg-note-amber/10 px-3 py-2 text-ink-2"
              style={{ fontSize: 12 }}
              data-testid={`payment-schedule-warning-${i}`}
            >
              {w}
            </div>
          ))}
          {errors.map((e, i) => (
            <div
              key={`e-${i}`}
              className="rounded-md border border-note-red/40 bg-note-red/10 px-3 py-2 text-note-red"
              style={{ fontSize: 12 }}
              data-testid={`payment-schedule-error-${i}`}
            >
              {e}
            </div>
          ))}
        </div>
      )}

      {/* Allocation editor (collapsible) */}
      <Card className="p-0" allowOverflow>
        <button
          type="button"
          onClick={() => setShowAllocations((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-surface-2"
          data-testid="payment-schedule-toggle-allocations"
        >
          <div>
            <div className="font-display text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
              Edit allocations
            </div>
            <div className="text-ink-3" style={{ fontSize: 11 }}>
              Change which milestone each phase or prov-sum keyword pays into. Changes save separately from the schedule settings above.
            </div>
          </div>
          {showAllocations
            ? <ChevronUp size={14} className="text-ink-3" />
            : <ChevronDown size={14} className="text-ink-3" />}
        </button>
        {showAllocations && editMilestones && (
          <div className="p-5 border-t border-line">
            <div className="mb-2 col-head" style={{ fontSize: 10 }}>Phases</div>
            <div className="flex flex-col divide-y divide-line-soft border border-line rounded-md mb-6">
              {nonSystemPhases.map((p) => (
                <AllocationRow
                  key={p.id}
                  kind="phase"
                  itemKey={p.id}
                  label={p.name || "(unnamed)"}
                  milestones={editMilestones}
                  isSplit={splitPhases.has(p.id) || nonZero("phase", p.id).length > 1}
                  onSetSingle={(mid) => setSingle("phase", p.id, mid)}
                  onSetSplit={(mid, pct) => setWeight("phase", p.id, mid, Math.max(0, Number(pct || 0) / 100))}
                  onEnterSplit={() => enterSplit("phase", p.id)}
                  onUnsplit={() => unsplit("phase", p.id)}
                  getWeight={(mid) =>
                    (editMilestones.find((m) => m.id === mid)?.claims || {})[p.id] || 0
                  }
                  total={totalFor("phase", p.id)}
                />
              ))}
              {nonSystemPhases.length === 0 && (
                <div className="px-3 py-4 text-center text-ink-3 italic" style={{ fontSize: 12 }}>
                  No non-system phases on this tender.
                </div>
              )}
            </div>

            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="col-head" style={{ fontSize: 10 }}>Prov-sum keywords</div>
                <p className="text-ink-3" style={{ fontSize: 10.5 }}>
                  Substring match against the prov-sum's description. A prov sum added AFTER this snapshot that matches a keyword here still lands on the right stage.
                </p>
              </div>
            </div>
            {(() => {
              const kwSeen = [];
              for (const m of editMilestones) {
                for (const k of Object.keys(m.claims_match || {})) {
                  if (!kwSeen.includes(k)) kwSeen.push(k);
                }
              }
              return (
                <div className="flex flex-col divide-y divide-line-soft border border-line rounded-md mb-3">
                  {kwSeen.map((kw) => (
                    <AllocationRow
                      key={kw}
                      kind="kw"
                      itemKey={kw}
                      label={`"${kw}"`}
                      milestones={editMilestones}
                      isSplit={splitKw.has(kw) || nonZero("kw", kw).length > 1}
                      onSetSingle={(mid) => setSingle("kw", kw, mid)}
                      onSetSplit={(mid, pct) => setWeight("kw", kw, mid, Math.max(0, Number(pct || 0) / 100))}
                      onEnterSplit={() => enterSplit("kw", kw)}
                      onUnsplit={() => unsplit("kw", kw)}
                      onRemove={() => removeKeyword(kw)}
                      getWeight={(mid) =>
                        (editMilestones.find((m) => m.id === mid)?.claims_match || {})[kw] || 0
                      }
                      total={totalFor("kw", kw)}
                    />
                  ))}
                  {kwSeen.length === 0 && (
                    <div className="px-3 py-3 text-center text-ink-3 italic" style={{ fontSize: 12 }}>
                      No prov-sum keywords in this snapshot yet.
                    </div>
                  )}
                </div>
              );
            })()}

            <KeywordAdder onAdd={addKeyword} milestones={editMilestones} />

            <div className="mt-6 flex items-center justify-end gap-3">
              {invalidItems.length > 0 && (
                <span
                  className="text-note-red"
                  style={{ fontSize: 12 }}
                  data-testid="payment-schedule-invalid-label"
                >
                  {invalidItems[0].label} is at {(invalidItems[0].total * 100).toFixed(1)}%
                </span>
              )}
              <PrimaryButton
                onClick={saveAllocations}
                disabled={
                  savingMilestones ||
                  !dirtyMilestones ||
                  invalidItems.length > 0
                }
                icon={savingMilestones ? Loader2 : Save}
                title={
                  invalidItems.length > 0
                    ? `${invalidItems[0].label} is at ${(invalidItems[0].total * 100).toFixed(1)}% — must total 100%`
                    : (!dirtyMilestones ? "No unsaved allocation changes" : "")
                }
                data-testid="payment-schedule-save-allocations"
              >
                Save allocations
              </PrimaryButton>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


/* Local AllocationRow — same UX shape as MilestonesTab's, adapted to
 * take a getWeight helper (since the tender snapshot's storage keys
 * are different for phase vs keyword). Kept inline rather than
 * shared to avoid a cross-file refactor for two callers. */

/* SplitPctInput — wrapper around NumberInput that holds its editing
 * value as a string LOCAL to the input, converting to the parent's
 * weight (0..1 proportion) only on change. The naive pattern
 *     value={(weight * 100).toFixed(1)}
 * reformats between keystrokes — typing "50" character by character
 * yields "5" → "5.0" (React re-renders "5.0" after the first digit)
 * → the second digit lands AFTER the decimal → 5.0%. The user has
 * typed 50 and the tender stores 5%. Same defect in the Settings
 * copy of this row. Hold the typed string here; reconcile only when
 * the external weight moves off what we're showing (save reset,
 * snap-and-drop, sibling clear). */
function SplitPctInput({ weight, onChange, ...rest }) {
  const canonical = (w) => {
    if (!Number.isFinite(w) || Math.abs(w) < 0.00001) return "";
    // Integer-math rounding to 1dp so 0.15 * 100 doesn't render as
    // "15.000000000000002" and the display never contains fp junk.
    const one_dp = Math.round(w * 1000) / 10;
    return String(one_dp);
  };
  const [txt, setTxt] = useState(() => canonical(weight));
  useEffect(() => {
    const shown = txt === "" ? 0 : Number(txt) / 100;
    if (Number.isNaN(shown) || Math.abs(shown - (weight || 0)) > 0.0005) {
      setTxt(canonical(weight));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight]);
  return (
    <NumberInput
      value={txt}
      onChange={(v) => {
        // NumberInput emits null on empty, Number otherwise. Preserve
        // the typed form locally so React never reformats it between
        // keystrokes; forward the pct number to the caller unchanged
        // (they own the /100 → weight step).
        setTxt(v === null || v === undefined ? "" : String(v));
        onChange(v);
      }}
      {...rest}
    />
  );
}


function AllocationRow({
  kind, itemKey, label, milestones, isSplit,
  onSetSingle, onSetSplit, onEnterSplit, onUnsplit, onRemove,
  getWeight, total,
}) {
  const totalPct = (total || 0) * 100;
  const totalOk = total < TOL || Math.abs(total - 1.0) < TOL;
  const totalLabel = total < TOL ? "0%" : `${totalPct.toFixed(1)}%`;
  const primaryId = (() => {
    const nz = milestones.filter((m) => (getWeight(m.id) || 0) > TOL);
    return nz.length === 1 ? nz[0].id : "";
  })();
  return (
    <div
      className="px-3 py-2.5 flex items-start justify-between gap-3"
      data-testid={`ps-allocation-row-${kind}-${itemKey}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cls(
              "text-ink flex-shrink-0",
              kind === "kw" && "italic text-ink-2",
            )}
            style={{ fontSize: 13 }}
          >{label}</span>
          <span
            className={cls(
              "rounded-full px-2 py-0.5 font-mono tnum flex-shrink-0",
              totalOk
                ? "bg-surface-2 text-ink-3"
                : "bg-note-red/10 text-note-red border border-note-red/30",
            )}
            style={{ fontSize: 10.5 }}
            data-testid={`ps-allocation-total-${kind}-${itemKey}`}
          >
            {totalLabel}
          </span>
        </div>
        {isSplit ? (
          <div className="mt-2 flex flex-col gap-1.5 pl-2 border-l border-line-soft">
            {milestones.map((m) => {
              const w = getWeight(m.id) || 0;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-2"
                  data-testid={`ps-allocation-split-input-${kind}-${itemKey}-${m.id}`}
                >
                  <span className="text-ink-3 flex-1 truncate" style={{ fontSize: 12 }}>
                    {m.title}
                  </span>
                  <SplitPctInput
                    weight={w}
                    onChange={(v) => onSetSplit(m.id, v)}
                    step="0.1"
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-1.5">
            <select
              value={primaryId}
              onChange={(e) => onSetSingle(e.target.value || null)}
              className="w-full rounded border border-line bg-surface px-2 py-1 text-ink"
              style={{ fontSize: 12.5 }}
              data-testid={`ps-allocation-single-select-${kind}-${itemKey}`}
            >
              <option value="">— Not allocated —</option>
              {milestones.map((m) => (
                <option key={m.id} value={m.id}>{m.title}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="flex-shrink-0 pt-0.5 flex items-center gap-1">
        {isSplit ? (
          <button
            type="button"
            onClick={onUnsplit}
            className="h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1"
            style={{ fontSize: 11 }}
            title="Un-split: snap the largest weight to 100% and drop the others (atomic)"
            data-testid={`ps-allocation-unsplit-${kind}-${itemKey}`}
          >
            Un-split
          </button>
        ) : (
          <button
            type="button"
            onClick={onEnterSplit}
            disabled={milestones.length < 2}
            className="h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: 11 }}
            title={
              milestones.length < 2
                ? "Snapshot has fewer than two milestones — nothing to split across"
                : "Split this item across several milestones"
            }
            data-testid={`ps-allocation-split-${kind}-${itemKey}`}
          >
            <GitBranch size={11} /> Split
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="h-7 w-7 rounded text-ink-3 hover:text-note-red hover:bg-surface-2 inline-flex items-center justify-center"
            title="Remove this keyword from the snapshot"
            data-testid={`ps-allocation-remove-${kind}-${itemKey}`}
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}


function KeywordAdder({ onAdd, milestones }) {
  const [kw, setKw] = useState("");
  const [mid, setMid] = useState("");
  const canAdd = kw.trim() && mid && milestones.length > 0;
  const submit = () => {
    if (!canAdd) return;
    onAdd(kw, mid);
    setKw("");
    setMid("");
  };
  return (
    <div className="flex items-end gap-2">
      <Field
        label="Add a prov-sum keyword"
        hint="Case-insensitive substring — matches any prov sum whose description contains this word."
      >
        <Input
          value={kw}
          onChange={setKw}
          placeholder="e.g. glazing"
          onKeyDown={(e) => e.key === "Enter" && submit()}
          data-testid="ps-keyword-add-input"
        />
      </Field>
      <Field label="Attach to milestone">
        <select
          value={mid}
          onChange={(e) => setMid(e.target.value)}
          disabled={milestones.length === 0}
          className="rounded border border-line bg-surface px-2 py-1 text-ink"
          style={{ fontSize: 13, minWidth: 220 }}
          data-testid="ps-keyword-add-milestone"
        >
          <option value="">
            {milestones.length === 0 ? "Snapshot has no milestones" : "Choose milestone…"}
          </option>
          {milestones.map((m) => (
            <option key={m.id} value={m.id}>{m.title}</option>
          ))}
        </select>
      </Field>
      <QuietButton
        onClick={submit}
        icon={Plus}
        disabled={!canAdd}
        data-testid="ps-keyword-add-btn"
      >
        Add
      </QuietButton>
    </div>
  );
}
